import { createGlobalStyle } from "styled-components";
import { setTypography } from "@topi/ui/styles/mixins";
import {
  borderRadius,
  topiFont,
  colors,
  transitions,
  fontWeights,
  brandCssProperties,
  topiBrandValues,
  BrandValueName,
  BrandValues,
} from "@topi/ui/styles/tokens/tokens";
import { resetReactToastify } from "@topi/ui/components/toast/toast.styles";

const brandNames = Object.keys(brandCssProperties) as BrandValueName[];

/**
 * Brand values come from the API as free strings. Only values the browser
 * parses as a color are emitted, so a malformed or hostile string can neither
 * break the stylesheet nor inject rules; the topi default stays in place.
 */
const isColor = (value: string): boolean =>
  typeof CSS !== "undefined" && CSS.supports("color", value);

const toCssProperties = (brandValues: BrandValues): string =>
  brandNames
    .filter((name) => {
      const value = brandValues[name];
      return value !== undefined && isColor(value);
    })
    .map((name) => `${brandCssProperties[name]}: ${brandValues[name]};`)
    .join("\n");

const topiBrandProperties = toCssProperties(topiBrandValues);

export const GlobalStyle = createGlobalStyle`
  :root {
    ${topiBrandProperties}
  }

  @font-face {
    font-family: "Readex Pro";
    src: url(${topiFont}) format("truetype-variations");
    /* The next line needs to be set or the font weights will be ignored (https://clagnut.com/blog/2390/) */
    font-weight: 1 999;
  }

  *,
  *::before, *::after,
  *:before, *:after {
    box-sizing: border-box;
  }

  body{
    background-color: ${colors.white};
    color: ${colors.grey90};
    ${setTypography("body1")}
    margin: 0;
    padding: 0;

    /* Hack to avoid a bug on webkit when using min-height: 100vh making a scrollbar
     appear due to margin collapsing yielding a longer height.
     See: https://stackoverflow.com/questions/48313396/html-body-padding-margin-100vh-and-calc
    */
    &::after {
      content: "" ;
      display: block;
      height: 0px ;
    }
  }

  p,
  dl, dd, dt,
  menu,
  ul, ol, li,
  h1, h2, h3, h4, h5, h6{
      padding: 0;
      margin: 0;
  }

  h1, h2, h3, h4, h5, h6 {
      font-weight:normal;
      font-size: 100%;
  }

  b, strong {
    font-weight: ${fontWeights.medium};
  }

  a {
    color: ${colors.brandPrimary};
    transition: ${transitions.fast} color;
    text-decoration: none;
    &:hover {
      color: ${colors.brandPrimaryHover};
      text-decoration: underline;
    }

    &:focus-visible {
      outline-color: ${colors.brandPrimary};
      border-radius: ${borderRadius.s};
    }
  }

  dl,
  dd,
  menu,
  ul {
      list-style: none;
  }

  ol{
      list-style-type: decimal;
  }

  img {
      max-width: 100%;
      height: auto;
      border-style: none;
  }

  fieldset {
    border: none;
    padding: 0;
    margin: 0;
    min-width: 0;
  }

  ${resetReactToastify}
`;

export interface BrandStyleProps {
  brandValues?: BrandValues;
}

/**
 * Overrides the topi brand colors with the ones a brand provides. Only the
 * colors present in `brandValues` are emitted; the rest keep the topi value
 * from `GlobalStyle`.
 *
 * Defined after `GlobalStyle` on purpose: styled-components orders global
 * styles by definition, so this block always comes later in the cascade and
 * wins over the topi defaults.
 */
export const BrandStyle = createGlobalStyle<BrandStyleProps>`
  :root {
    ${({ brandValues = {} }): string => toCssProperties(brandValues)}
  }
`;
